/**
 * Changelog entries for LinCal
 * 
 * AGENT INSTRUCTIONS:
 * When creating a release, add a new entry at the TOP of this array.
 * Consolidate git commits into user-friendly highlights (3-6 bullet points).
 * Focus on features and fixes users care about, not internal refactors.
 * 
 * Entry format (bilingual):
 * {
 *   version: 'X.Y.Z',
 *   date: 'YYYY-MM-DD',
 *   title: { en: 'English title', de: 'German title' },
 *   highlights: {
 *     en: ['English highlight 1', 'English highlight 2', ...],
 *     de: ['German highlight 1', 'German highlight 2', ...]
 *   }
 * }
 */
export const changelog = [
  {
    version: '2.0.0-rc2',
    date: '2026-01-27',
    title: {
      en: 'UI Polish & Bug Fixes',
      de: 'UI-Verbesserungen & Fehlerbehebungen'
    },
    highlights: {
      en: [
        'Improved calendar cell aspect ratio for better visual balance',
        'Reduced spacing below events and milestones for more compact rows',
        'Matched milestone height with event bars for consistency',
        'Translated default "Personal" project name to user language',
        'Auto-merge local data on first login when server calendar is empty',
        'Fixed duplicate project display in header after creation'
      ],
      de: [
        'Verbessertes Seitenverhältnis der Kalenderzellen für bessere Optik',
        'Reduzierter Abstand unter Events und Meilensteinen für kompaktere Zeilen',
        'Meilenstein-Höhe an Event-Balken angeglichen für Konsistenz',
        'Standard-Projekt "Persönlich" in Benutzersprache übersetzt',
        'Auto-Merge lokaler Daten bei Erstanmeldung wenn Server-Kalender leer',
        'Doppelte Projekt-Anzeige im Header nach Erstellung behoben'
      ]
    }
  },
  {
    version: '2.0.0-rc1',
    date: '2026-01-26',
    title: {
      en: 'Multi-User & Collaboration',
      de: 'Mehrbenutzerbetrieb & Zusammenarbeit'
    },
    highlights: {
      en: [
        'User accounts with OIDC authentication (Authentik)',
        'Share calendars and projects with other users (view or edit access)',
        'Real-time sync across all devices via WebSocket',
        'Multiple calendars per user with easy switcher',
        'Subscribe to calendars via iCal feed in external apps',
        'Offline support with automatic reconnection',
        'Automatic database setup and migrations',
        'Production-ready Docker deployment with security hardening'
      ],
      de: [
        'Benutzerkonten mit OIDC-Authentifizierung (Authentik)',
        'Kalender und Projekte mit anderen Nutzern teilen (Ansicht oder Bearbeitung)',
        'Echtzeit-Synchronisation über alle Geräte via WebSocket',
        'Mehrere Kalender pro Benutzer mit einfachem Umschalter',
        'Kalender-Abo via iCal-Feed in externen Apps',
        'Offline-Unterstützung mit automatischer Wiederverbindung',
        'Automatische Datenbank-Einrichtung und Migrationen',
        'Produktionsreife Docker-Bereitstellung mit Sicherheitshärtung'
      ]
    }
  },
  {
    version: '1.0.0-rc1',
    date: '2026-01-12',
    title: {
      en: 'Release Candidate 1',
      de: 'Release-Kandidat 1'
    },
    highlights: {
      en: [
        'Export to iCalendar (.ics) format for use with other calendar apps',
        'Multi-language support: English and German with auto-detection',
        'Delete projects with option to merge events into another project',
        'Improved milestone positioning between events',
        'Consolidated import/export options in Settings',
        'Many bug fixes and internal improvements',
      ],
      de: [
        'Export ins iCalendar-Format (.ics) zur Nutzung mit anderen Kalender-Apps',
        'Mehrsprachigkeit: Englisch und Deutsch mit automatischer Erkennung',
        'Projekte löschen mit Option zum Zusammenführen von Ereignissen',
        'Verbesserte Meilenstein-Positionierung zwischen Ereignissen',
        'Konsolidierte Import/Export-Optionen in den Einstellungen',
        'Viele Fehlerbehebungen und interne Verbesserungen',
      ]
    }
  },
  {
    version: '0.9.0',
    date: '2026-01-10',
    title: {
      en: 'Modern UI & Smart Zoom',
      de: 'Modernes UI & Intelligenter Zoom'
    },
    highlights: {
      en: [
        'Redesigned interface with a modern, polished look',
        'Year progress indicator shows how much of the year has passed',
        'Smart zoom automatically fills viewport width on load and resize',
        'Unified tooltip styling across all elements',
        'Fixed multi-month event drag behavior',
        'Improved project pill interactions in header',
      ],
      de: [
        'Neugestaltete Oberfläche mit modernem, elegantem Look',
        'Jahresfortschrittsanzeige zeigt, wie viel des Jahres vergangen ist',
        'Intelligenter Zoom passt sich automatisch an die Fensterbreite an',
        'Einheitliches Tooltip-Design für alle Elemente',
        'Korrigiertes Drag-Verhalten für mehrmonatige Ereignisse',
        'Verbesserte Interaktion mit Projekt-Pills in der Kopfzeile',
      ]
    }
  },
  {
    version: '0.8.0',
    date: '2026-01-10',
    title: {
      en: 'Initial Public Release',
      de: 'Erste öffentliche Version'
    },
    highlights: {
      en: [
        'Full year calendar view with zoom and pan',
        'Create events by dragging across days',
        'Milestones with customizable icons',
        'Organize with colored projects',
        'Holiday management for non-working days',
        'Import/export your data as JSON backup',
      ],
      de: [
        'Jahreskalenderansicht mit Zoom und Schwenken',
        'Erstelle Ereignisse durch Ziehen über Tage',
        'Meilensteine mit anpassbaren Icons',
        'Organisation mit farbigen Projekten',
        'Feiertagsverwaltung für arbeitsfreie Tage',
        'Import/Export deiner Daten als JSON-Backup',
      ]
    }
  }
];

// Current version is always the first entry
export const currentVersion = changelog[0].version;

/**
 * Get all changelog entries since a given version
 * @param {string} lastSeenVersion - The last version the user saw
 * @returns {Array} Array of changelog entries newer than lastSeenVersion
 */
export function getVersionsSince(lastSeenVersion) {
  if (!lastSeenVersion) return changelog;
  
  const index = changelog.findIndex(v => v.version === lastSeenVersion);
  if (index === -1) return changelog; // Unknown version, show all
  if (index === 0) return []; // Already on latest
  
  return changelog.slice(0, index); // All versions newer than lastSeen
}
